/* eslint-disable */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../firebase";

function valueToMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  if (typeof value._seconds === "number") return value._seconds * 1000;

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function currentActor() {
  return {
    uid: auth.currentUser?.uid || "unknown",
    email: auth.currentUser?.email || "",
  };
}

export function subscribeToSchools(onData, onError) {
  const schoolsQuery = query(collection(db, "schools"));

  return onSnapshot(
    schoolsQuery,
    (snapshot) => {
      const rows = snapshot.docs.map((schoolDocument) => ({
        id: schoolDocument.id,
        ...schoolDocument.data(),
      }));

      rows.sort((a, b) => {
        const aDate =
          a.activatedAt || a.adminActivatedAt || a.createdAt || a.updatedAt;
        const bDate =
          b.activatedAt || b.adminActivatedAt || b.createdAt || b.updatedAt;

        return valueToMillis(bDate) - valueToMillis(aDate);
      });

      onData(rows);
    },
    onError
  );
}

export async function updateSchoolAccountProfile(schoolId, profile) {
  if (!schoolId) throw new Error("The school account ID is missing.");

  const actor = currentActor();

  await updateDoc(doc(db, "schools", schoolId), {
    planName: profile.plan || null,
    renewalDate: profile.renewalDate || null,
    onboardingStatus: profile.onboardingStatus || "not_started",
    billingStatus: profile.billingStatus || "not_set",
    supportStatus: profile.supportStatus || "normal",
    accountOwner: profile.accountOwner || "",
    accountUpdatedAt: serverTimestamp(),
    accountUpdatedBy: actor.uid,
    accountUpdatedByEmail: actor.email,
  });
}

export async function updateSchoolLifecycleStatus(
  schoolId,
  {
    nextStatus,
    previousStatus,
    reason = "",
    notes = "",
    schoolName = "",
  }
) {
  if (!schoolId) throw new Error("The school account ID is missing.");
  if (!["live", "paused", "suspended", "archived"].includes(nextStatus)) {
    throw new Error("The requested lifecycle status is invalid.");
  }

  const actor = currentActor();

  await updateDoc(doc(db, "schools", schoolId), {
    lifecycleStatus: nextStatus,
    lifecycleReason: reason || null,
    lifecycleNotes: notes || null,
    lifecycleUpdatedAt: serverTimestamp(),
    lifecycleUpdatedBy: actor.uid,
    lifecycleUpdatedByEmail: actor.email,
  });

  await addDoc(collection(db, "schoolAccountAudit"), {
    type: "lifecycle",
    schoolId,
    schoolName,
    previousStatus: previousStatus || "pending",
    nextStatus,
    reason: reason || "",
    notes: notes || "",
    createdAt: serverTimestamp(),
    createdBy: actor.uid,
    createdByEmail: actor.email,
  });
}

export async function addSchoolAccountNote(
  schoolId,
  { note, schoolName = "" }
) {
  if (!schoolId) throw new Error("The school account ID is missing.");
  if (!note?.trim()) throw new Error("Enter an internal account note.");

  const actor = currentActor();

  await addDoc(collection(db, "schoolAccountAudit"), {
    type: "note",
    schoolId,
    schoolName,
    note: note.trim(),
    createdAt: serverTimestamp(),
    createdBy: actor.uid,
    createdByEmail: actor.email,
  });
}

export function subscribeToSchoolLifecycleHistory(
  schoolId,
  onData,
  onError
) {
  if (!schoolId) {
    onData([]);
    return () => {};
  }

  const auditQuery = query(collection(db, "schoolAccountAudit"));

  return onSnapshot(
    auditQuery,
    (snapshot) => {
      const rows = snapshot.docs
        .map((auditDocument) => ({
          id: auditDocument.id,
          ...auditDocument.data(),
        }))
        .filter((entry) => entry.schoolId === schoolId)
        .sort((a, b) => valueToMillis(b.createdAt) - valueToMillis(a.createdAt));

      onData(rows);
    },
    onError
  );
}

export async function deleteSchoolAccountPermanently(
  schoolId,
  { schoolName = "" } = {}
) {
  if (!schoolId) throw new Error("The school account ID is missing.");

  const actor = currentActor();
  if (!auth.currentUser) {
    throw new Error("You must be signed in to delete a school account.");
  }

  /*
    This removes the customer account document and its CoreLogic account-audit
    entries. It deliberately does not attempt to delete Authentication users,
    school operational collections, Storage files or unrelated records.
  */
  const auditSnapshot = await getDocs(
    query(
      collection(db, "schoolAccountAudit"),
      where("schoolId", "==", schoolId)
    )
  );

  const batch = writeBatch(db);

  auditSnapshot.docs.forEach((auditDocument) => {
    batch.delete(auditDocument.ref);
  });

  batch.delete(doc(db, "schools", schoolId));
  await batch.commit();

  console.info("School account permanently deleted", {
    schoolId,
    schoolName,
    deletedAuditEntries: auditSnapshot.size,
    deletedBy: actor.uid,
    deletedByEmail: actor.email,
  });
}

